import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import { AuthProvider } from '@shared/components/auth/AuthProvider';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import './index.css';

// Initialize Sentry for error tracking
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const appEnv = import.meta.env.VITE_APP_ENV || 'development';

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: appEnv,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance monitoring
    tracesSampleRate: appEnv === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev
    // Session replay
    replaysSessionSampleRate: appEnv === 'production' ? 0.1 : 0, // 10% of sessions in prod
    replaysOnErrorSampleRate: 1.0, // 100% on errors
    // Only send errors in production
    enabled: appEnv !== 'development' || !!sentryDsn,
    // Filter out local errors during development
    beforeSend(event) {
      // Don't send errors in development unless explicitly enabled
      if (appEnv === 'development' && !sentryDsn) {
        return null;
      }
      return event;
    },
  });
}

createRoot(document.getElementById('root')!).render(
  <ErrorBoundary>
    <BrowserRouter>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <AuthProvider>
          <App />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  </ErrorBoundary>
);
